import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CalendarDays, Clock, Users, Pencil, FileText } from 'lucide-react';
import AddAtelierDialog from '../../components/AddAtelierDialog';

const AnimateurAteliersView = ({
  ateliers,
  users,
  onEditAtelier,
  onOpenEmargement,
  onRefresh
}) => {
  return (
    <>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900">Gestion des Ateliers & Événements</h2>
        <AddAtelierDialog onSuccess={onRefresh} />
      </div>

      {ateliers.length === 0 ? (
        <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
          <CardContent className="text-center py-12">
            <CalendarDays className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun atelier planifié</p>
            <p className="text-sm text-slate-400 mt-2">Créez un nouvel atelier pour commencer</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {ateliers.map((atelier) => {
            const animateursList = users.filter(u => atelier.animateurs_ids?.includes(u.id));
            return (
              <Card key={atelier.id} className="bg-white border-slate-200 rounded-3xl shadow-sm overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base sm:text-xl font-bold mb-1 sm:mb-2 leading-tight">{atelier.titre}</CardTitle>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-blue-100 text-xs sm:text-sm">
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-3 h-3 sm:w-4 sm:h-4" />
                          {atelier.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                          {atelier.heure_debut} - {atelier.heure_fin}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => onEditAtelier(atelier)}
                        className="bg-white/20 hover:bg-white/30 text-white rounded-xl text-xs sm:text-sm px-2 sm:px-3"
                      >
                        <Pencil className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Modifier</span>
                      </Button>
                      <div className="px-2 py-1 bg-white/20 rounded-full text-xs font-bold hidden sm:block">
                        {atelier.type_atelier}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="mb-4">
                    <p className="text-sm font-bold text-slate-700 mb-2">Intervenant(s)</p>
                    <div className="flex flex-wrap gap-2">
                      {animateursList.length > 0 ? animateursList.map((a) => (
                        <span key={a.id} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                          {a.prenom} {a.nom}
                        </span>
                      )) : (
                        <span className="text-slate-400 text-sm">Aucun</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-700 mb-2">
                      Participants ({atelier.participants?.length || 0})
                    </p>
                    <div className="space-y-1 max-h-[150px] overflow-y-auto">
                      {atelier.participants?.filter(p => p.prenom || p.nom).map((p) => {
                        const isPresent = p.statut_presence === 'present' || p.present === true;
                        const isNonDefini = p.statut_presence === 'non_defini' || p.present === null || (p.present !== true && p.present !== false && !p.statut_presence);
                        return (
                          <div key={p.id} className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                            isPresent ? 'bg-emerald-50' : isNonDefini ? 'bg-slate-50' : 'bg-red-50'
                          }`}>
                            <span>{p.prenom} {p.nom}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                              isPresent ? 'bg-emerald-100 text-emerald-700' : isNonDefini ? 'bg-slate-200 text-slate-600' : 'bg-red-100 text-red-700'
                            }`}>
                              {isPresent ? 'Présent' : isNonDefini ? 'Non défini' : 'Absent'}
                            </span>
                          </div>
                        );
                      })}
                      {atelier.participants?.filter(p => !p.prenom && !p.nom).length > 0 && (
                        <p className="text-xs text-slate-400 italic">
                          + {atelier.participants.filter(p => !p.prenom && !p.nom).length} participant(s) non identifié(s)
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button
                      onClick={() => onOpenEmargement(atelier)}
                      size="sm"
                      className="rounded-xl bg-amber-600 hover:bg-amber-700"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Émargement
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
};

export default AnimateurAteliersView;
